/**
 * The keyboards of a product line (alphabets, max characters, latin font and
 * weight), the special characters, symbols and letter cases allowed.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';
import {
  ALLOWED_CASES,
  ALLOWED_EXCLUSIVE_SYMBOLS,
  ALLOWED_ICONIC_SYMBOLS,
  AVAILABLE_FONTS,
  KEYBOARDS,
  SPECIAL_CHARS,
  type KeyboardKey,
} from '@/shared/constants';
import { appConfigQueryKey, fetchAppConfig } from '@/shared/config/app-config';
import { Dialog } from '@/shared/ui/Dialog';
import {
  productLineQueryOptions,
  updateProductLine,
  type ProductLine,
  type ProductLineKeyboard,
} from './api';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface KeyboardsDialogProps {
  item: ProductLine;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

// Traditional chinese keyboards are not offered for now.
const HIDDEN_KEYBOARDS: readonly string[] = ['chineseTw', 'chineseHk'];

const fontOptions = optionsOf(Object.keys(AVAILABLE_FONTS));

const fontWeightOptions = (font: string | null | undefined): string[] =>
  font && AVAILABLE_FONTS[font] ? [...AVAILABLE_FONTS[font]] : [];

type OptionMap =
  | 'specialChars'
  | 'allowedCases'
  | 'allowedIconicSymbols'
  | 'allowedExclusiveSymbols';

export function KeyboardsDialog({
  item,
  onClose,
}: Readonly<KeyboardsDialogProps>) {
  const queryClient = useQueryClient();
  const productLine = useQuery(productLineQueryOptions(item.id));
  // The dialog edits a copy of the fetched `keyboard` object.
  const [edits, setEdits] = useState<ProductLineKeyboard | null>(null);
  const keyboard: ProductLineKeyboard | null =
    edits ?? (productLine.data ? (productLine.data.keyboard ?? {}) : null);
  const setKeyboard = (
    update: (prev: ProductLineKeyboard | null) => ProductLineKeyboard | null,
  ) => setEdits(update(keyboard));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (productLine.isError)
      toast.error('An error occurred while loading the product line');
  }, [productLine.isError]);

  const save = useMutation({
    mutationFn: async () => {
      await updateProductLine({ id: item.id, keyboard });
      await fetchAppConfig();
      await queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
    },
    onSuccess: () => onClose('Confirm'),
    onError: () => setErrorMessage('Failed to update product line'),
  });

  const keyboards = keyboard
    ? (Object.keys(KEYBOARDS) as KeyboardKey[]).filter(
        (key) => keyboard[key] && !HIDDEN_KEYBOARDS.includes(key),
      )
    : [];

  const setEntry = (
    key: KeyboardKey,
    patch: Record<string, string | boolean | null>,
  ) =>
    setKeyboard((prev) =>
      prev ? { ...prev, [key]: { ...(prev[key] ?? {}), ...patch } } : prev,
    );

  const setOption = (map: OptionMap, key: string, checked: boolean) =>
    setKeyboard((prev) =>
      prev
        ? { ...prev, [map]: { ...(prev[map] ?? {}), [key]: checked } }
        : prev,
    );

  const onFontChange = (font: string) => {
    const weights = fontWeightOptions(font);
    setEntry('latin', {
      font: font || null,
      fontWeight: weights[0] ?? '',
    });
  };

  const optionCheckbox = (
    map: OptionMap,
    key: string,
    label: React.ReactNode,
    domId: string,
    group: string,
  ) => (
    <div className="styled-input-square styled-input-single mb-0" key={domId}>
      <input
        type="checkbox"
        id={domId}
        name={domId}
        data-testid={`product-line-keyboards-form-${group}-${kebabCase(key)}`}
        checked={!!keyboard?.[map]?.[key]}
        onChange={(event) => setOption(map, key, event.target.checked)}
      />
      <label htmlFor={domId}>{label}</label>
    </div>
  );

  return (
    <Dialog
      title="Keyboards"
      onClose={() => onClose('Cancel')}
      width="1000px"
      testId="product-line-keyboards-dialog"
    >
      <form
        className="form popin-form popin-keyboard"
        onSubmit={(event) => {
          event.preventDefault();
          setErrorMessage(null);
          save.mutate();
        }}
        noValidate
      >
        {keyboard ? (
          <div className="bs-row mb-1">
            <div className="col-span-9">
              <div className="table-container">
                <div
                  className="items-container"
                  style={{ overflow: 'visible' }}
                >
                  <table className="table items">
                    <thead>
                      <tr>
                        <th style={{ width: '35%' }}>Alphabet</th>
                        <th style={{ width: '20%' }}>
                          Max Number of Characters
                        </th>
                        <th style={{ width: '25%' }}>Font</th>
                        <th style={{ width: '20%' }}>Font Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyboards.map((key) => {
                        const entry = keyboard[key] ?? {};
                        const isLatin = key === 'latin';
                        const weights = fontWeightOptions(entry.font);
                        return (
                          <tr
                            key={key}
                            data-testid="product-line-keyboards-form-keyboard-row"
                            data-value={key}
                          >
                            <td style={{ width: '35%' }} title={KEYBOARDS[key]}>
                              <span
                                className="styled-input-square styled-input-single"
                                style={{
                                  display: 'inline-block',
                                  textAlign: 'center',
                                  maxWidth: 140,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  id={`keyboardActive-${key}`}
                                  name={`keyboardActive-${key}`}
                                  data-testid={`product-line-keyboards-form-${kebabCase(key)}-active`}
                                  checked={!!entry.active}
                                  disabled={isLatin}
                                  onChange={(event) =>
                                    setEntry(key, {
                                      active: event.target.checked,
                                    })
                                  }
                                />
                                <label htmlFor={`keyboardActive-${key}`}>
                                  {KEYBOARDS[key]}
                                </label>
                              </span>
                            </td>
                            <td
                              style={{ width: '20%' }}
                              title={String(entry.maxChars ?? '')}
                            >
                              <input
                                className="field"
                                aria-label={`${KEYBOARDS[key]} max number of characters`}
                                data-testid={`product-line-keyboards-form-${kebabCase(key)}-max-chars`}
                                value={String(entry.maxChars ?? '')}
                                onChange={(event) =>
                                  setEntry(key, {
                                    maxChars: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td
                              style={{ width: '25%', overflow: 'visible' }}
                              title={entry.font ?? ''}
                            >
                              {isLatin ? (
                                <div className="field field-selector">
                                  <Select
                                    aria-label="Latin font"
                                    placeholder="--Select font--"
                                    options={fontOptions}
                                    value={entry.font ?? ''}
                                    onChange={onFontChange}
                                    testId="product-line-keyboards-form-latin-font"
                                  />
                                </div>
                              ) : (
                                <div
                                  data-testid={`product-line-keyboards-form-${kebabCase(key)}-font`}
                                >
                                  {entry.font}
                                </div>
                              )}
                            </td>
                            <td
                              style={{ width: '20%', overflow: 'visible' }}
                              title={entry.fontWeight ?? ''}
                            >
                              {isLatin && weights.length > 1 ? (
                                <div className="field field-selector">
                                  <Select
                                    aria-label="Latin font weight"
                                    placeholder="--Select font weight--"
                                    options={optionsOf(weights)}
                                    value={entry.fontWeight ?? ''}
                                    onChange={(fontWeight) =>
                                      setEntry('latin', { fontWeight })
                                    }
                                    testId="product-line-keyboards-form-latin-font-weight"
                                  />
                                </div>
                              ) : (
                                entry.fontWeight
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-span-3">
              <div className="mb-4">
                <u>Special characters allowed:</u>
              </div>
              {Object.entries(SPECIAL_CHARS).map(([key, label]) =>
                optionCheckbox(
                  'specialChars',
                  key,
                  label,
                  `specialCharActive-${key}`,
                  'special-char',
                ),
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                {Object.entries(ALLOWED_ICONIC_SYMBOLS).map(([key, code]) =>
                  optionCheckbox(
                    'allowedIconicSymbols',
                    key,
                    <>
                      <span className={`symbol-${code}`} aria-hidden="true" />
                      <span className="sr-only">{key}</span>
                    </>,
                    `allowedIconicSymbolActive-${key}`,
                    'iconic-symbol',
                  ),
                )}
                {ALLOWED_EXCLUSIVE_SYMBOLS.map((symbol) =>
                  optionCheckbox(
                    'allowedExclusiveSymbols',
                    symbol.key,
                    <>
                      <span
                        className={`symbol-${symbol.code}`}
                        aria-hidden="true"
                      />
                      <span className="sr-only">{symbol.value}</span>
                    </>,
                    `allowedExclusiveSymbolActive-${symbol.key}`,
                    'exclusive-symbol',
                  ),
                )}
              </div>
              <br />
              <div className="mb-4">
                <u>Letter case allowed:</u>
              </div>
              {Object.entries(ALLOWED_CASES).map(([key, label]) =>
                optionCheckbox(
                  'allowedCases',
                  key,
                  label,
                  `allowedCaseActive-${key}`,
                  'case',
                ),
              )}
            </div>
          </div>
        ) : (
          <div className="mb-1 flex justify-center">
            <FontAwesomeIcon icon={faCircleNotch} spin aria-label="Loading" />
          </div>
        )}

        {errorMessage && (
          <div
            className="alert mb-4"
            role="alert"
            data-testid="product-line-keyboards-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-2 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            disabled={save.isPending || !keyboard}
            data-testid="product-line-keyboards-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
